'use client';

import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import type { ComponentProps } from 'react';
import { HorizontalScrollContainer } from '../HorizontalScrollContainer/HorizontalScrollContainer';
import { PageToolbar } from '../PageToolbar/PageToolbar';
import { SubnavigationItem } from '../SubnavigationItem/SubnavigationItem';
import { navigationRegistry } from './navigationConfig';
import type { ProductVertical } from './navigationConfig/types';

type PageToolbarWithNavigationProps = Omit<ComponentProps<typeof PageToolbar>, 'title'> & {
  namespace: string;
  productVertical: ProductVertical;
  path?: string;
  onItemClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

function PageToolbarWithNavigation({ namespace, productVertical, path: pathOverride, className, children, onItemClick, ...props }: PageToolbarWithNavigationProps) {
  const t = useTranslations(namespace);
  const pathname = usePathname();
  const path = pathOverride ?? pathname;
  const { navigationTabs, sectionMatchers, staticNavOnPages } = navigationRegistry[productVertical];
  const isStatic = staticNavOnPages ? staticNavOnPages.includes(path) : false;
  const currentNavItem = sectionMatchers.find(([pattern]) => new RegExp(pattern).test(path))?.[1];

  return (
    <PageToolbar title={t('title')} className={twMerge(isStatic && 'static md:sticky', className)} {...props}>
      {children}
      <HorizontalScrollContainer className="px-2 grid gap-0 grid-flow-col auto-cols-[minmax(5rem,1fr)] sm:auto-cols-[5.75rem] sm:justify-center" data-track-container="Menu">
        {navigationTabs.map(({ href, icon, id, trackName, mobileOnly }, index) => {
          const nextTab = navigationTabs[index + 1];
          const showDivider = index < navigationTabs.length - 1;
          const hideDividerOnDesktop = showDivider && nextTab?.mobileOnly;

          return (
            <SubnavigationItem
              onClick={onItemClick}
              key={id}
              href={href}
              icon={icon}
              text={t(`tabs.${id}`)}
              isActive={id === currentNavItem}
              showDivider={showDivider}
              trackName={trackName}
              className={twMerge(mobileOnly ? 'md:hidden' : undefined, hideDividerOnDesktop ? 'md:after:hidden' : undefined)}
            />
          );
        })}
      </HorizontalScrollContainer>
    </PageToolbar>
  );
}

export { PageToolbarWithNavigation };
